/**
 * Metafield sorting for collection pages.
 *
 * Shopify doesn't support sorting a collection by an arbitrary metafield (native `sort_by` only
 * accepts a fixed list of keys: manual, best-selling, title-*, price-*, created-*). To offer a
 * "Weight capacity: Low to high" style option driven by a product metafield, this script:
 *
 *   1. Lets the "Metafield sort option" blocks (see sections/main-collection-products.liquid)
 *      add fake sort_by values shaped like `metafield:<blockId>` to the existing sort dropdown.
 *   2. When one of those is chosen, waits for the normal facet-filters fetch to settle (Shopify
 *      ignores the unrecognized sort_by and just returns page 1 in its default order), then
 *      fetches every remaining page of the CURRENT filtered result set via the Section Rendering
 *      API, reads each product's metafield value off a `data-mf-<blockId>` attribute (rendered
 *      server-side by main-collection-products.liquid), and re-sorts the whole grid client-side.
 *   3. Also runs on page load if the URL already contains `?sort_by=metafield:...` (e.g. from a
 *      bookmarked/shared link), so it works without a prior interaction.
 *
 * Trade-off: this loads every product in the current filter/search result up to the configured
 * cap (section setting "Metafield sort: max products", default 1000) in order to sort correctly,
 * since Shopify has no server-side way to do it. For very large filtered result sets this means
 * more network requests than a normal paginated view. If a result set exceeds the cap, a notice
 * is shown and only the first batch is sorted.
 *
 * Upload to: assets/metafield-sort.js
 * Also requires the edits in: sections/main-collection-products.liquid, snippets/products-toolbar.liquid,
 * snippets/facet-filters.liquid (script tag + config/data attributes added there).
 */
(function () {
  if (window.__mfSortInit) return;
  window.__mfSortInit = true;

  const SORT_PREFIX = 'metafield:';
  const DEFAULT_MAX_PRODUCTS = 1000;
  const DEFAULT_PER_PAGE = 16;
  const BASE_SORT = 'created-descending'; // used while fetching pages; irrelevant to the final order

  function getHost() {
    return document.querySelector('custom-pagination[data-metafield-sort-config]');
  }

  function getConfig(host) {
    if (!host) return [];
    try {
      return JSON.parse(host.getAttribute('data-metafield-sort-config') || '[]');
    } catch (err) {
      console.warn('metafield-sort.js: could not parse sort config', err); // eslint-disable-line
      return [];
    }
  }

  function numericValue(el, id) {
    const raw = el.getAttribute(`data-mf-${id}`);
    if (raw === null || raw === '') return null;
    const n = parseFloat(raw);
    return Number.isNaN(n) ? null : n;
  }

  async function fetchResultsFragment(url) {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`metafield-sort.js: fetch failed with status ${res.status}`);
    const html = await res.text();
    const tmpl = document.createElement('template');
    tmpl.innerHTML = html;
    return tmpl.content;
  }

  function showNotice(resultsEl, message) {
    let notice = resultsEl.querySelector('.js-mf-sort-notice');
    if (!message) {
      if (notice) notice.remove();
      return;
    }
    if (!notice) {
      notice = document.createElement('p');
      notice.className = 'js-mf-sort-notice text-sm mt-4';
      resultsEl.appendChild(notice);
    }
    notice.textContent = message;
  }

  async function runSort(sortValue) {
    if (!sortValue || !sortValue.startsWith(SORT_PREFIX)) return;
    const blockId = sortValue.slice(SORT_PREFIX.length);

    const host = getHost();
    if (!host) return;
    const config = getConfig(host);
    const entry = config.find((c) => c.id === blockId);
    if (!entry) return;

    const resultsEl = document.getElementById('filter-results');
    const list = resultsEl && resultsEl.querySelector('ul');
    if (!resultsEl || !list) return;

    const perPage = parseInt(host.dataset.metafieldSortPerPage, 10) || DEFAULT_PER_PAGE;
    const maxProducts = parseInt(host.dataset.metafieldSortMaxProducts, 10) || DEFAULT_MAX_PRODUCTS;
    const maxPages = Math.max(1, Math.ceil(maxProducts / perPage));
    const sectionId = document.getElementById('facets')?.dataset.filterSectionId;

    resultsEl.classList.add('is-loading');

    const baseParams = new URLSearchParams(window.location.search);
    baseParams.set('sort_by', BASE_SORT);

    function pageUrl(pageNum) {
      const p = new URLSearchParams(baseParams);
      p.set('page', String(pageNum));
      let url = `${window.location.pathname}?${p.toString()}`;
      if (sectionId) url += `&section_id=${sectionId}`;
      return url;
    }

    function itemsFromFragment(fragment) {
      return Array.from(fragment.querySelectorAll('#filter-results .js-pagination-result'));
    }

    // Fetch a batch of pages concurrently (rather than one at a time) - this is what was making
    // large collections take 15-30s. A small concurrency cap avoids firing 30+ requests at once.
    async function fetchPagesConcurrently(pageNumbers, concurrency) {
      const results = new Array(pageNumbers.length);
      let cursor = 0;
      async function worker() {
        while (cursor < pageNumbers.length) {
          const i = cursor;
          cursor += 1;
          results[i] = itemsFromFragment(await fetchResultsFragment(pageUrl(pageNumbers[i])));
        }
      }
      await Promise.all(new Array(Math.min(concurrency, pageNumbers.length)).fill(0).map(worker));
      return results;
    }

    let allItems = [];
    let truncated = false;

    try {
      // Page 1 tells us how many results there are in total (via data-num-results on the
      // section), so we know how many more pages to fetch instead of finding out one at a time.
      const firstFragment = await fetchResultsFragment(pageUrl(1));
      allItems = itemsFromFragment(firstFragment);

      const countEl = firstFragment.querySelector('[data-num-results]');
      const totalResults = countEl ? parseInt(countEl.getAttribute('data-num-results'), 10) : NaN;

      let totalPages;
      if (!Number.isNaN(totalResults)) {
        totalPages = Math.max(1, Math.ceil(totalResults / perPage));
      } else {
        // Fallback if the count wasn't found: trust the pagination link on page 1.
        const paginationEl = firstFragment.querySelector('.js-pagination');
        const hasMore = paginationEl ? paginationEl.dataset.isMoreResults === 'true' : false;
        totalPages = hasMore ? maxPages : 1;
      }

      if (totalPages > maxPages) {
        totalPages = maxPages;
        truncated = true;
      }

      if (totalPages > 1) {
        const remainingPages = [];
        for (let p = 2; p <= totalPages; p += 1) remainingPages.push(p);
        const batches = await fetchPagesConcurrently(remainingPages, 6);
        batches.forEach((items) => {
          allItems = allItems.concat(items);
        });
      }
    } catch (err) {
      console.warn('metafield-sort.js: sort failed, leaving default order in place.', err); // eslint-disable-line
      resultsEl.classList.remove('is-loading');
      return;
    }

    try {
      const dir = entry.direction === 'desc' ? -1 : 1;
      allItems.sort((a, b) => {
        const av = numericValue(a, blockId);
        const bv = numericValue(b, blockId);
        if (av === null && bv === null) return 0;
        if (av === null) return 1; // items missing the metafield always sink to the bottom
        if (bv === null) return -1;
        return (av - bv) * dir;
      });

      // IMPORTANT: clear the grid's current (unsorted) contents before re-appending the fetched
      // items in sorted order - appendChild alone just adds after what's already there.
      list.innerHTML = '';
      allItems.forEach((item) => list.appendChild(item));

      // We've now loaded and rendered everything ourselves - remove native pagination controls.
      const nativePagination = resultsEl.querySelector('nav[aria-label]');
      if (nativePagination) nativePagination.remove();
      host.dataset.pauseInfiniteScroll = 'true';

      showNotice(
        resultsEl,
        truncated
          ? `Showing the first ${allItems.length} results sorted by "${entry.label}". Narrow your filters to sort the rest.`
          : null
      );
    } catch (err) {
      console.warn('metafield-sort.js: error while rendering sorted results.', err); // eslint-disable-line
    } finally {
      resultsEl.classList.remove('is-loading');
    }
  }

  // The desktop sort widget is a <custom-select> wrapping a hidden native <select name="sort_by">
  // (see snippets/custom-select.liquid). Choosing an option fires TWO separate change-ish events:
  // a plain 'change' on the hidden native select, AND a separate CustomEvent('change') on the
  // <custom-select> element itself (see assets/custom-select.js). Mobile sort uses plain radio
  // inputs named "sort_by", which fire both 'input' and 'change'. facet-filters.js listens for
  // all of these and, for a metafield value, would fetch Shopify with a sort_by it can't
  // understand and silently overwrite our sorted grid right after we render it. So instead of
  // racing it, we intercept every one of these events in the capture phase (before they reach
  // facet-filters.js's listeners) and take over completely for metafield selections.
  function isSortByTarget(target) {
    if (!target) return false;
    if (target.name === 'sort_by') return true; // native select + mobile radios
    if (target.id === 'products-sort-by') return true; // the <custom-select> element itself
    return false;
  }

  function valueFromEvent(evt) {
    const target = evt.target;
    if (target && target.tagName === 'CUSTOM-SELECT') {
      return evt.detail && evt.detail.selectedValue;
    }
    return target && target.value;
  }

  function buildParamsFromCurrentFilters() {
    const form = document.getElementById('facets');
    const params = new URLSearchParams(form ? new FormData(form) : undefined);
    Array.from(params.keys()).forEach((key) => {
      if (!params.get(key)) params.delete(key);
    });
    return params;
  }

  function init() {
    let pending = null;

    document.addEventListener(
      'change',
      (evt) => {
        if (!isSortByTarget(evt.target)) return;
        const value = valueFromEvent(evt);
        if (!value || !value.startsWith(SORT_PREFIX)) return;

        // Stop this from ever reaching facet-filters.js - it can't do anything useful with a
        // metafield sort value, and would otherwise clobber our sorted result right after.
        evt.stopImmediatePropagation();

        // The native select's plain 'change' and the custom-select's own CustomEvent('change')
        // both land here for the same click; coalesce them into a single run.
        clearTimeout(pending);
        pending = setTimeout(() => {
          // Keep mobile radios and the desktop control in sync with each other.
          document.querySelectorAll('.js-drawer-sort-by').forEach((radio) => {
            radio.checked = radio.value === value;
          });

          const params = buildParamsFromCurrentFilters();
          params.set('sort_by', value);
          window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);

          runSort(value);
        }, 30);
      },
      true // capture phase - runs before facet-filters.js's own (bubble-phase) listeners
    );

    // Support landing directly on a URL that already requests a metafield sort.
    const initialSort = new URLSearchParams(window.location.search).get('sort_by');
    if (initialSort && initialSort.startsWith(SORT_PREFIX)) {
      runSort(initialSort);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();